import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { productSearchService } from "../../../../services/productSearchService";
import { adminProductsV3Service } from "../../../../services/adminProductsV3Service";
import type { AdminProductSearchItem } from "../../../../types/productV3";
import { isNewSnapshotShape } from "../../../../types/productV3";
import type { ProductMetadata } from "../../../../types/productMetadata";
import type { Visibility } from "../../../../types/subscription";

const PAGE_SIZE = 1000;

export type AdminFilterTab = "all" | "baskets" | "portfolios" | "managedStrategies";
export type AdminSortField =
  | "id"
  | "productType"
  | "title"
  | "created"
  | "provider"
  | "visibility"
  | "longCount"
  | "shortCount"
  | "optionCount"
  | "grossExposure"
  | "netExposure"
  | "updated";

export interface AdminProductItem {
  productType: "basket" | "portfolio" | "managedStrategy";
  id: number;
  title: string;
  description: string | null;
  visibility: string;
  providerId: number | null;
  providerName: string;
  stockCount: number;
  optionCount: number;
  basketCount: number;
  longCount: number;
  shortCount: number;
  grossExposure: number;
  netExposure: number;
  createdAtUtc: string;
  updatedAtUtc: string;
  settings: ProductMetadata | null;
  contractRemarks: string[];
  raw: AdminProductSearchItem;
}

/** Normalizes the raw contractRemarks payload: trimmed, lowercased, de-duped. */
export function normalizeContractRemarks(
  remarks: string[] | null | undefined
): string[] {
  if (!Array.isArray(remarks)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of remarks) {
    if (typeof raw !== "string") continue;
    const remark = raw.trim().toLowerCase();
    if (!remark || seen.has(remark)) continue;
    seen.add(remark);
    out.push(remark);
  }
  return out;
}

/** Splits the comma-separated remarks filter box into normalized tokens. */
export function parseRemarksFilterTokens(value: string): string[] {
  const seen = new Set<string>();
  const tokens: string[] = [];
  for (const part of value.split(",")) {
    const token = part.trim().toLowerCase();
    if (!token || seen.has(token)) continue;
    seen.add(token);
    tokens.push(token);
  }
  return tokens;
}

/**
 * Substring match of the filter tokens against the product's remarks. A product
 * matches when *any* token matches any of its remarks (OR).
 */
export function matchesRemarksFilter(remarks: string[], tokens: string[]): boolean {
  if (tokens.length === 0) return true;
  return tokens.some((token) => remarks.some((r) => r.includes(token)));
}

const CASH_CONID = 141175785;
// Client-side-only sentinel for Managed Strategy's null providerId. Never sent
// to the API -- all Products-page filtering happens over the already-fetched
// allProducts array (see fetchAllSearchPages), so this only needs to be a
// stable, non-colliding key for de-dup/sort/filter inside this hook.
const PLUTUS_PROVIDER_KEY = -1;

function deriveCompositionStats(item: AdminProductSearchItem): {
  longCount: number;
  shortCount: number;
  optionCount: number;
  grossExposure: number;
  netExposure: number;
} {
  // A Managed Strategy's `activated` shape is `{ composition: [...] }` (no
  // `contracts`) -- isNewSnapshotShape() only checks for the presence of
  // `activated`, not `contracts`, so it returns true for that shape too. A
  // Managed Strategy holds products, not stocks/options directly, so treat
  // it the same as the legacy/no-snapshot fallback (all-zero composition
  // stats; see docs/specs/PWA-2347-admin-managed-strategy-products-2026-07-02.md
  // section on syncManagedStrategySearch).
  if (!isNewSnapshotShape(item.snapshot) || !("contracts" in item.snapshot.activated)) {
    return { longCount: 0, shortCount: 0, optionCount: item.numOfOptions, grossExposure: 0, netExposure: 0 };
  }

  const allStocks = item.snapshot.activated.contracts.stocks ?? [];
  const stocks = allStocks.filter((s) => s.conId !== CASH_CONID);
  let longCount = 0;
  let shortCount = 0;
  let longExposure = 0;
  let shortExposure = 0;
  for (const s of stocks) {
    if (s.weight > 0) {
      longCount++;
      longExposure += Math.abs(s.weight);
    } else if (s.weight < 0) {
      shortCount++;
      shortExposure += Math.abs(s.weight);
    }
  }

  let optCount = 0;
  if ("options" in item.snapshot.activated.contracts) {
    const options = (item.snapshot.activated.contracts as any).options ?? [];
    optCount = options.length;
    for (const o of options) {
      if (o.weight > 0) longExposure += Math.abs(o.weight);
      else if (o.weight < 0) shortExposure += Math.abs(o.weight);
    }
  }

  if ("baskets" in item.snapshot.activated) {
    const baskets = (item.snapshot.activated as any).baskets ?? [];
    for (const b of baskets) {
      if (b.weight > 0) longExposure += Math.abs(b.weight);
      else if (b.weight < 0) shortExposure += Math.abs(b.weight);
    }
  }

  return {
    longCount,
    shortCount,
    optionCount: optCount,
    grossExposure: longExposure + shortExposure,
    netExposure: longExposure - shortExposure,
  };
}

function toAdminProduct(item: AdminProductSearchItem): AdminProductItem {
  const stats = deriveCompositionStats(item);
  const productType =
    item.productType === "BASKET"
      ? "basket"
      : item.productType === "PORTFOLIO"
      ? "portfolio"
      : "managedStrategy";
  return {
    productType,
    id: item.productId,
    title: item.title,
    description: item.description,
    visibility: item.visibility,
    providerId: item.providerId,
    providerName: item.providerName,
    stockCount: item.numOfStocks,
    optionCount: stats.optionCount,
    basketCount:
      productType === "managedStrategy"
        ? item.numOfBaskets + item.numOfPortfolios
        : item.numOfBaskets,
    longCount: stats.longCount,
    shortCount: stats.shortCount,
    grossExposure: stats.grossExposure,
    netExposure: stats.netExposure,
    createdAtUtc: item.createdAtUtc,
    updatedAtUtc: item.updatedAtUtc,
    settings: (item.settings as ProductMetadata) ?? null,
    contractRemarks: normalizeContractRemarks(item.contractRemarks),
    raw: item,
  };
}

async function fetchAllSearchPages(): Promise<AdminProductSearchItem[]> {
  const first = await productSearchService.searchAdminProductsAllTypes({ page: 1, pageSize: PAGE_SIZE });
  const all = [...first.items];
  if (first.totalPages <= 1) return stabilizeOrder(all);
  const remaining = await Promise.all(
    Array.from({ length: first.totalPages - 1 }, (_, i) =>
      productSearchService.searchAdminProductsAllTypes({ page: i + 2, pageSize: PAGE_SIZE })
    )
  );
  for (const r of remaining) all.push(...r.items);
  return stabilizeOrder(all);
}

function stabilizeOrder(items: AdminProductSearchItem[]): AdminProductSearchItem[] {
  const seen = new Set<string>();
  const unique = items.filter((item) => {
    const key = `${item.productType}-${item.productId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return unique.sort((a, b) => {
    const provCmp = (a.providerId ?? PLUTUS_PROVIDER_KEY) - (b.providerId ?? PLUTUS_PROVIDER_KEY);
    if (provCmp !== 0) return provCmp;
    const idCmp = a.productId - b.productId;
    if (idCmp !== 0) return idCmp;
    return a.productType.localeCompare(b.productType);
  });
}

function getSortValue(item: AdminProductItem, field: AdminSortField): string | number {
  switch (field) {
    case "id": return item.id;
    case "productType": return item.productType;
    case "title": return item.title;
    case "created": return new Date(item.createdAtUtc).getTime();
    case "provider": return item.providerName;
    case "visibility": return item.visibility;
    case "longCount": return item.longCount;
    case "shortCount": return item.shortCount;
    case "optionCount": return item.optionCount;
    case "grossExposure": return item.grossExposure;
    case "netExposure": return item.netExposure;
    case "updated": return new Date(item.updatedAtUtc).getTime();
  }
}

function sortProducts(items: AdminProductItem[], field: AdminSortField, desc: boolean): AdminProductItem[] {
  return [...items].sort((a, b) => {
    const aVal = getSortValue(a, field);
    const bVal = getSortValue(b, field);
    let cmp: number;
    if (typeof aVal === "string" && typeof bVal === "string") {
      cmp = aVal.localeCompare(bVal);
    } else {
      cmp = (aVal as number) - (bVal as number);
    }
    if (cmp !== 0) return desc ? -cmp : cmp;
    const provTie = (a.providerId ?? Number.NEGATIVE_INFINITY) - (b.providerId ?? Number.NEGATIVE_INFINITY);
    if (provTie !== 0) return provTie;
    return a.id - b.id;
  });
}

export interface UseAdminProductsListReturn {
  products: AdminProductItem[];
  allProducts: AdminProductItem[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  basketCount: number;
  portfolioCount: number;
  managedStrategyCount: number;
  filterTab: AdminFilterTab;
  setFilterTab: (tab: AdminFilterTab) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  providerFilter: number | null;
  setProviderFilter: (id: number | null) => void;
  visibilityFilter: string | null;
  setVisibilityFilter: (v: string | null) => void;
  remarksFilter: string;
  setRemarksFilter: (value: string) => void;
  sortField: AdminSortField;
  sortDesc: boolean;
  handleSort: (field: AdminSortField) => void;
  providers: { id: number; name: string }[];
  reload: () => void;
  updateVisibility: (product: AdminProductItem, next: Visibility) => Promise<void>;
  isVisibilitySaving: (product: AdminProductItem) => boolean;
  /**
   * Monotonically increments on every successful server reload. Downstream
   * hooks (e.g. useAdminProductMetadata) depend on this to clear session-local
   * overrides only when the server data is authoritative again, not on every
   * allProducts array-identity change caused by optimistic local updates.
   */
  dataVersion: number;
}

function visibilityKey(product: AdminProductItem): string {
  return `${product.productType}-${product.providerId}-${product.id}`;
}

export function useAdminProductsList(): UseAdminProductsListReturn {
  const [searchParams] = useSearchParams();
  const [allProducts, setAllProducts] = useState<AdminProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterTab, setFilterTab] = useState<AdminFilterTab>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [providerFilter, setProviderFilter] = useState<number | null>(null);
  const [visibilityFilter, setVisibilityFilter] = useState<string | null>(null);
  const [remarksFilter, setRemarksFilter] = useState("");
  const [sortField, setSortField] = useState<AdminSortField>("updated");
  const [sortDesc, setSortDesc] = useState(true);
  const [visibilitySavingKeys, setVisibilitySavingKeys] = useState<Set<string>>(new Set());
  const [dataVersion, setDataVersion] = useState(0);
  const loadIdRef = useRef(0);

  const load = useCallback(async () => {
    const loadId = ++loadIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const items = await fetchAllSearchPages();
      if (loadIdRef.current !== loadId) return;
      setAllProducts(items.map(toAdminProduct));
      setDataVersion((v) => v + 1);
    } catch (err) {
      if (loadIdRef.current !== loadId) return;
      console.error("Failed to load admin products:", err);
      setError("Failed to load products. Please try again.");
    } finally {
      if (loadIdRef.current === loadId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const providerIdParam = searchParams.get("providerId");
    const parsedProviderId = providerIdParam ? Number(providerIdParam) : null;
    setProviderFilter(
      parsedProviderId !== null && Number.isInteger(parsedProviderId) && parsedProviderId > 0
        ? parsedProviderId
        : null
    );

    const typeParam = searchParams.get("type");
    if (typeParam === "basket") {
      setFilterTab("baskets");
    } else if (typeParam === "portfolio") {
      setFilterTab("portfolios");
    } else if (typeParam === "managedStrategy") {
      setFilterTab("managedStrategies");
    } else {
      setFilterTab("all");
    }
  }, [searchParams]);

  const providers = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of allProducts) {
      const key = p.providerId ?? PLUTUS_PROVIDER_KEY;
      if (!map.has(key)) map.set(key, p.providerName);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allProducts]);

  const remarksTokens = useMemo(
    () => parseRemarksFilterTokens(remarksFilter),
    [remarksFilter]
  );

  const filtered = useMemo(() => {
    return allProducts.filter((p) => {
      if (filterTab === "baskets" && p.productType !== "basket") return false;
      if (filterTab === "portfolios" && p.productType !== "portfolio") return false;
      if (filterTab === "managedStrategies" && p.productType !== "managedStrategy") return false;
      if (providerFilter !== null && (p.providerId ?? PLUTUS_PROVIDER_KEY) !== providerFilter) return false;
      if (visibilityFilter !== null && p.visibility !== visibilityFilter) return false;
      if (!matchesRemarksFilter(p.contractRemarks, remarksTokens)) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const titleMatch = p.title.toLowerCase().includes(term);
        const descMatch = p.description?.toLowerCase().includes(term) ?? false;
        const provMatch = p.providerName.toLowerCase().includes(term);
        const idMatch = String(p.id) === term;
        if (!titleMatch && !descMatch && !provMatch && !idMatch) return false;
      }
      return true;
    });
  }, [
    allProducts,
    filterTab,
    providerFilter,
    visibilityFilter,
    searchTerm,
    remarksTokens,
  ]);

  const sorted = useMemo(
    () => sortProducts(filtered, sortField, sortDesc),
    [filtered, sortField, sortDesc]
  );

  const totalCount = filtered.length;
  const basketCount = filtered.filter((p) => p.productType === "basket").length;
  const portfolioCount = filtered.filter((p) => p.productType === "portfolio").length;
  const managedStrategyCount = filtered.filter((p) => p.productType === "managedStrategy").length;

  const handleSort = useCallback(
    (field: AdminSortField) => {
      if (sortField === field) {
        setSortDesc((prev) => !prev);
      } else {
        setSortField(field);
        setSortDesc(true);
      }
    },
    [sortField]
  );

  const updateVisibility = useCallback(
    async (product: AdminProductItem, next: Visibility) => {
      const key = visibilityKey(product);
      const previous = product.visibility;
      if (previous === next) return;

      setAllProducts((prev) =>
        prev.map((p) =>
          visibilityKey(p) === key ? { ...p, visibility: next } : p
        )
      );
      setVisibilitySavingKeys((prev) => new Set(prev).add(key));

      try {
        await adminProductsV3Service.patchProductVisibility(
          product.productType,
          product.providerId,
          product.id,
          next
        );
      } catch (err) {
        console.error("Failed to update product visibility:", err);
        setAllProducts((prev) =>
          prev.map((p) =>
            visibilityKey(p) === key ? { ...p, visibility: previous } : p
          )
        );
        throw err;
      } finally {
        setVisibilitySavingKeys((prev) => {
          const nextSet = new Set(prev);
          nextSet.delete(key);
          return nextSet;
        });
      }
    },
    []
  );

  const isVisibilitySaving = useCallback(
    (product: AdminProductItem) => visibilitySavingKeys.has(visibilityKey(product)),
    [visibilitySavingKeys]
  );

  return {
    products: sorted,
    allProducts,
    loading,
    error,
    totalCount,
    basketCount,
    portfolioCount,
    managedStrategyCount,
    filterTab,
    setFilterTab,
    searchTerm,
    setSearchTerm,
    providerFilter,
    setProviderFilter,
    visibilityFilter,
    setVisibilityFilter,
    remarksFilter,
    setRemarksFilter,
    sortField,
    sortDesc,
    handleSort,
    providers,
    reload: load,
    updateVisibility,
    isVisibilitySaving,
    dataVersion,
  };
}
